import { OwnUpTextInput } from '@rategravity/own-up-component-library';
import { DollarIcon } from '@rategravity/own-up-component-library/icon-library/system-icons/standard-icons/dollar';
import React, { useCallback } from 'react';

import { NumericFormat } from 'react-number-format';
import { LabelWithIcon } from '../../common/info-label';
import { useCalculator } from '../data/dataProvider';
import { AffordabilityDataActionsTypes } from '../types';

type BasicInputs = {
  annualIncome: number;
  monthlyDebt: number;
  downPayment: number;
};

type InputEvent = {
  inputTitle: keyof BasicInputs;
  value: number;
};

const actionMapping = {
  annualIncome: AffordabilityDataActionsTypes.SET_ANNUAL_INCOME,
  monthlyDebt: AffordabilityDataActionsTypes.SET_TOTAL_MONTHLY_DEBT,
  downPayment: AffordabilityDataActionsTypes.SET_DOWN_PAYMENT
} as const;

export const BasicInputsComponent = ({
  inputs,
  updateInputs,
  onClick
}: {
  inputs: BasicInputs;
  updateInputs: (inputs: InputEvent) => void;
  onClick: () => void;
}) => {
  const { annualIncome, monthlyDebt, downPayment } = inputs;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={onClick}>
      <NumericFormat
        customInput={OwnUpTextInput}
        label={<LabelWithIcon label="Annual income total" />}
        value={annualIncome}
        labelPosition="outer"
        $leadingIcon={DollarIcon()}
        decimalScale={0}
        allowNegative={false}
        thousandSeparator
        onChange={(e) => {
          updateInputs({
            inputTitle: 'annualIncome',
            value: Number(e.target.value.replace(/[^0-9.-]/g, ''))
          });
        }}
      />
      <NumericFormat
        customInput={OwnUpTextInput}
        label={<LabelWithIcon label="Total monthly debt" />}
        value={monthlyDebt}
        labelPosition="outer"
        $leadingIcon={DollarIcon()}
        decimalScale={0}
        allowNegative={false}
        thousandSeparator
        onChange={(e) => {
          updateInputs({
            inputTitle: 'monthlyDebt',
            value: Number(e.target.value.replace(/[^0-9.-]/g, ''))
          });
        }}
      />
      <NumericFormat
        customInput={OwnUpTextInput}
        label={<LabelWithIcon label="Down payment amount" />}
        value={downPayment}
        labelPosition="outer"
        $leadingIcon={DollarIcon()}
        decimalScale={0}
        allowNegative={false}
        thousandSeparator
        onChange={(e) => {
          updateInputs({
            inputTitle: 'downPayment',
            value: Number(e.target.value.replace(/[^0-9.-]/g, ''))
          });
        }}
      />
    </div>
  );
};

export const BasicInputs = () => {
  const {
    state: { annualIncome, downPayment, monthlyDebt },
    dispatch
  } = useCalculator();
  const updateInputs = useCallback(
    (inputs: InputEvent) => {
      const actionType = actionMapping[inputs.inputTitle];
      if (actionType) {
        dispatch({ type: actionType, payload: inputs.value });
      }
    },
    [dispatch]
  );
  const openAdvancedOptions = useCallback(() => {
    dispatch({ type: AffordabilityDataActionsTypes.SET_SHOW_FULL, payload: true });
  }, [dispatch]);
  return (
    <BasicInputsComponent
      inputs={{ downPayment, annualIncome, monthlyDebt }}
      updateInputs={updateInputs}
      onClick={openAdvancedOptions}
    />
  );
};
